import dayjs from "dayjs"
import { convertToDateRaw } from "../../helper/helper"
import { Organisation, OrganisationModel } from "../organisation/OrganisationModelV2"
import { Person } from "../person/Person"
import { TransactorOrganisation, buildTransactorOrganisationDetail } from "../person/TransactorOrganisation"
import { ContractHarvest, buildContractHarvestForDashBoard, buildContractHarvestForDetail, buildContractHarvestForExport } from "./ContractHarvest"
import { ContractInterface } from "./ContractInterface"
import { buildReceiptPhotos, ContractPhoto } from "./ContractPhoto"
import { ContractProcessedValues } from "./ContractProcessedValues"
import { ContractSaleStatement, buildContractSaleStatementForDetail, buildContractSaleStatementFromSale } from "./ContractSaleStatement"
import { Harvest } from "./Harvest"
import { SaleStatement } from "./SaleStatement"
import { Organisation as OrganisationV2 } from "../organisation/OrganisationModelV2"

export class Contract implements ContractInterface {
    id: number
    status: string
    market_day?: string
    invoiceId?: number
    sale_statements?: ContractSaleStatement[]
    harvests?: ContractHarvest[]
    transporter?: TransactorOrganisation
    primary_buyer_id?: number
    primary_buyer?: TransactorOrganisation
    agent?: TransactorOrganisation
    sellers?: TransactorOrganisation[]
    quantityGross?: number
    percentageFruitInOut?: number
    percentagePPPunchQuantityInProgram?: number
    nbHarvestBoosted?: number
    grading?: number
    netQuantity?: number
    contract_type?: string
    processData?: ContractProcessedValues
    isDashBoardData?: Boolean
    totalUnileverBonusPaid?: number
    totalquantityWithUnileverBonus?: number

    constructor(id: number,
        status: string,
        market_day?: string,
        sale_statements?: ContractSaleStatement[],
        harvests?: ContractHarvest[],
        transporter?: TransactorOrganisation,
        primary_buyer_id?: number,
        primary_buyer?: TransactorOrganisation,
        invoice_id?: number,
        quantityGross?: number,
        percentageFruitInOut?: number,
        percentagePPPunchQuantityInProgram?: number,
        nbHarvestBoosted?: number,
        grading?: number,
        agent?: TransactorOrganisation,
        sellers?: TransactorOrganisation[],
        netQuantity?: number,
        contract_type?: string,
        isDashBoardContract: boolean = false,
        totalUnileverBonusPaid?: number,
        totalquantityWithUnileverBonus?: number) {
        this.id = id
        this.status = status
        this.market_day = market_day
        this.sale_statements = sale_statements
        this.harvests = harvests
        this.transporter = transporter
        this.primary_buyer_id = primary_buyer_id
        this.primary_buyer = primary_buyer
        this.invoiceId = invoice_id
        this.quantityGross = quantityGross
        this.percentageFruitInOut = percentageFruitInOut
        this.percentagePPPunchQuantityInProgram = percentagePPPunchQuantityInProgram
        this.nbHarvestBoosted = nbHarvestBoosted
        this.grading = grading
        this.agent = agent
        this.sellers = sellers
        this.netQuantity = netQuantity
        this.contract_type = contract_type
        this.isDashBoardData = isDashBoardContract
        this.totalUnileverBonusPaid = totalUnileverBonusPaid
        this.totalquantityWithUnileverBonus = totalquantityWithUnileverBonus
        if (isDashBoardContract) {
            this.processData = new ContractProcessedValues(this)
        }
    }
    getType(): string {
        return this.contract_type
    }
    getBuyer(): Organisation {
        return OrganisationV2.build(this.primary_buyer.organisation)
    }
    getPersonAgent(): Person | undefined {
        return this.agent?.transactor.person
    }
    getPersonTransporter(): Person | undefined {
        return this.transporter?.transactor?.person
    }
    getAgent(): Organisation | undefined {
        return OrganisationV2.build(this.agent.organisation)
    }

    harvestPhotos(): ContractPhoto[] {
        return this.harvests?.find(h => h.harvest.photos).harvest.photos
    }

    receiptPhotos(): ContractPhoto[] {
        return buildReceiptPhotos(this.getBuyerSaleStatement()?.photos)
    }

    getId(): number {
        return this.id
    }

    getStatus(): string {
        return this.status
    }

    getTotal(): number {
        throw new Error("Method not implemented.")
    }

    getHarvest(): Harvest[] {
        return this.harvests?.map(h => h.harvest)
    }

    getMargetDay(): string {
        return this.market_day
    }

    getBuyerSaleStatement(): SaleStatement {
        return this.sale_statements?.find((s) => s?.party === "buyer")?.sale_statement
    }

    isAgentContract(): boolean {
        return this.agent !== undefined
    }

    isHakamContract(): boolean {
        return this.contract_type.toLowerCase() === "ngpam"
    }

    isAtMillContract() {
        return this.isHakamContract() || this.isNamContract()
    }

    getSaleStatement(): SaleStatement {
        return this.sale_statements?.at(0)?.sale_statement
    }

    getSaleStatementHandler(): SaleStatement {
        return this.sale_statements?.find((s) => s?.party === "handler")?.sale_statement
    }

    getSaleStatementAgent(): SaleStatement {
        return this.sale_statements?.find((s) => s?.party === "agent")?.sale_statement
    }

    getSaleStatementSeller(): SaleStatement {
        return this.sale_statements?.find((s) => s?.party === "seller")?.sale_statement
    }

    getSaleStatementBuyer(): SaleStatement {
        return this.sale_statements?.find((s) => s?.party === "buyer")?.sale_statement
    }

    getMarketDay(): string {
        if (this.market_day) {
            return convertToDateRaw(this.market_day, "DD MMMM YYYY")
        } else {
            return "undefined"
        }
    }

    getTransporter() {
        return this.isAgentContract() ? this.agent : this.isHakamContract() ? this.sellers?.at(0) : this.transporter
    }

    getMarketDayUtc(): string {
        return dayjs(this.market_day).utc().format("DD MMMM YYYY")
    }

    getDeliveryDate(): string | undefined {
        const photos = this.getSaleStatement().photos
        if (photos.at(0)?.created_at) {
            return photos.at(0).created_at
        }
    }

    isNamContract(): boolean {
        return this.contract_type.toLowerCase() === "nam"
    }

    isNac() {
        return this.contract_type === "NAC"
    }

    // getFruitQuantity(): number {
    //     return this.processData.fruitQuantity
    // }

    // getUnileverFruitQuantity(): number {
    //     return this.processData.ulFruitQuantity
    // }

    // getNonUnileverFruitQuantity(): number {
    //     return this.processData.nonUlFruitQuantity
    // }

    // isUnileverContract(): boolean {
    //     return this.nbHarvestBoosted > 0
    // }

    // getFarmerPersonIds(): number[] {
    //     return this.processData.farmerIds
    // }

    // getFarmersDeclared(): Person[] {
    //     return this.processData.farmersDeclared
    // }

    // getFarmersUndeclared(): Person[] {
    //     return this.processData.farmersUndeclared
    // }

    // getUnileverFarmerPersonIds(): number[] {
    //     return this.processData.ulFarmerIds
    // }

    // getNonUnileverFarmerPersonIds(): number[] {
    //     return this.processData.nonUlFarmerIds
    // }

    // getLandPlotsId(): number[] {
    //     return this.harvests.map(h => h.harvest.land_plot.id)
    // }

    // getUnileverLandPlotsId(): number[] {
    //     return this.harvests.filter(h => h.harvest.isUnileverLandPlot()).map(h => h.harvest.land_plot.id)
    // }

    // getNonUnileverLandPlotsId(): number[] {
    //     return this.harvests.filter(h => !h.harvest.isUnileverLandPlot()).map(h => { return h.harvest.land_plot.id })
    // }

    // getLandPlots(): Landplot[] {
    //     return this.processData.landplots
    // }

    // getUnileverLandPlots(): Landplot[] {
    //     return this.processData.ulLandplots
    // }

    // getUnileverHarvest(): Harvest[] {
    //     return this.processData.ulHarvest
    // }

    // getNonUnileverHarvest(): Harvest[] {
    //     return this.processData.nonUlHarvest
    // }

    getHarvests(): Harvest[] {
        if (this.isDashBoardData) {
            return this.processData.harvests
        } else {
            return this.harvests?.map(h => h.harvest)
        }
    }

    // getFcrop(): number {
    //     return this.processData.fcrop
    // }

    // getPpunch(): number {
    //     return this.totalUnileverBonusPaid
    // }

    // isContractPpunched(): boolean {
    //     return this.harvests.find(h => h.harvest.isPpunched()) !== undefined
    // }

    // getTotalNetRp(): number {
    //     return this.processData.totalNetRp
    // }
}

export function buildContractForDashBoard(c: any): Contract {
    var harvests: ContractHarvest[]
    if (c.quantityGross && (c.type.toLowerCase() === "nam" || c.type.toLowerCase() === "ngpam")) {
        // Here we don't always know the quantity for each landplot for Nam's contracts, so we take the all harvest quantity and for
        // each harvest we check the proportion on all the harvest. And we apply it on the gross quantity to see how much it represent. 
        //PO-5515
        const qTotalHarvests = c.harvests.reduce((v, h) => v + (h.harvest.quantityFarmerAfterGrading ?? 0), 0)
        const qTotalBonus = c.harvests.reduce((v, h) => v + (h.ppunchTotal ?? 0), 0)
        if (qTotalHarvests > 0) {
            c.harvests.forEach(h => {
                const qTotalH = h.harvest.quantityFarmerAfterGrading
                h.harvest.quantityFarmerAfterGrading = (qTotalH / qTotalHarvests) * c.quantityGross
                h.ppunchTotal = (h.ppunchTotal / qTotalBonus) * c.totalUnileverBonusPaid
            })
        }
    }

    harvests = c.harvests.map((ch) => buildContractHarvestForDashBoard({ ...ch, contractId: c.id, grading: c.grading }))
    return new Contract(c.id,
        c.status,
        c.marketDate,
        null,
        harvests,
        null,
        c.primary_buyer?.organisation?.id,
        buildTransactorOrganisationDetail({ transactor: c.primary_buyer.transactor, organisation: { ...c.primary_buyer.organisation, isBuyerInProgram: c.isBuyerInProgram } }),
        c.invoice_item?.invoice_id,
        c.quantityGross,
        c.percentageQuantityInProgram,
        c.percentagePPPunchQuantityInProgram,
        c.nbHarvestBoosted,
        c.grading,
        undefined,
        undefined,
        undefined,
        c.type,
        true,
        c.totalUnileverBonusPaid,
        c.totalquantityWithUnileverBonus)
}

export function buildContractForList(c: any): Contract {
    return new Contract(c.id,
        c.status,
        c.market_day,
        c.sale_statements.map((css) => buildContractSaleStatementForDetail(css)),
        c.harvests?.map((ch) => buildContractHarvestForDetail(ch)),
        null,
        c.primary_buyer_id,
        buildTransactorOrganisationDetail(c.primary_buyer),
        c.invoice_item?.invoice_id)
}

export function buildContractForDetailFromHasura(c: any): Contract {
    return new Contract(
        c.id,
        c.status,
        c.market_day,
        c.ss.map(s => buildContractSaleStatementForDetail(s)),
        c.harvests?.map((ch) => buildContractHarvestForDetail({ harvest: ch })),
        c.agent_person ? buildTransactorOrganisationDetail(c.transporter.party_context) : undefined,
        c.primary_buyer_id,
        buildTransactorOrganisationDetail(c.buyer),
        c.invoice_item?.invoice_id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        c.contract_type
    )
}

export function buildContractForDetail(c: any): Contract {
    return new Contract(
        c.contract_id,
        c.status,
        c.market_day,
        [buildContractSaleStatementFromSale({ ...c.statement.statement, party: "buyer" })],
        c.harvests?.map((ch) => buildContractHarvestForDetail({ harvest: ch })),
        c.agent_person ? buildTransactorOrganisationDetail(c.transporter.party_context) : undefined,
        c.primary_buyer_id,
        buildTransactorOrganisationDetail(c.buyer),
        c.invoice_item?.invoice_id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        c.agent ? buildTransactorOrganisationDetail(c.agent) : undefined,
        c.sellers?.map(s => buildTransactorOrganisationDetail(s.party_context)),
        undefined,
        c.contract_type
    )
}

export function buildContractHakamForDetail(c: any): Contract {

    return new Contract(
        c.contract_id,
        c.status,
        c.market_day,
        [buildContractSaleStatementFromSale({ ...c.statement.statement, party: "buyer" })],
        c.harvests?.map((ch) => {
            const newHarvest = ch
            newHarvest.photos = ch.photoCollection?.map(newPhoto => {
                return {
                    photo: {
                        ...newPhoto
                    }
                }
            })
            return buildContractHarvestForDetail({ harvest: newHarvest })
        }),
        c.transporter ? buildTransactorOrganisationDetail(c.transporter.party_context) : undefined,
        c.primary_buyer_id,
        buildTransactorOrganisationDetail(c.primary_buyer.party_context),
        c.invoice_item?.invoice_id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        c.agent ? buildTransactorOrganisationDetail(c.agent.party_context) : undefined,
        c.sellers?.map(s => buildTransactorOrganisationDetail(s.party_context)),
        undefined,
        c.contract_type
    )
}


export function buildContractNafV3ForDetail(c: any): Contract {
    c.harvests.forEach(h => {
        h.photos = h.photos.map((p: any) => { return { photo: p } })
    })
    c.statement.photos = c.statement.photos.map(p => { return { photo: p } })
    return new Contract(
        c.contract_id,
        c.status,
        c.market_day,
        [buildContractSaleStatementFromSale({ ...c.statement, party: "buyer" })],
        c.harvests?.map((ch) => buildContractHarvestForDetail({ harvest: ch })),
        c.transporter ? buildTransactorOrganisationDetail({ transactor: c.transporter }) : undefined,
        c.buyer?.id,
        buildTransactorOrganisationDetail(c.buyer),
        c.invoice_item?.invoice_id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        c.agent ? buildTransactorOrganisationDetail(c.agent.party_context) : undefined,
        c.sellers?.map(s => buildTransactorOrganisationDetail(s.party_context)),
        undefined,
        c.contract_type
    )
}


export function buildContractForExport(c: any): Contract {
    return new Contract(
        c.contract_id,
        undefined,
        c.market_day,
        undefined,
        c.harvests?.map((ch) => buildContractHarvestForExport({ harvest: ch })),
        c.transporter ? buildTransactorOrganisationDetail(c.transporter) : undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        c.net_quantity
    )

}